import { Search, X, AlertCircle, ChevronRight } from "lucide-react";

/**
 * "Extracted Data Fields" heading + count + required-empty pill, and the
 * search / extraction-status filter / All-Mandatory-Custom segmented
 * control beneath it. Shared by the Normal, NetSuite and Business Central
 * request views. Purely presentational — every count/value is computed by
 * the caller, since each view groups its fields differently.
 */
export default function FieldsPanelHeader({
  fieldCount,
  emptyRequiredCount = 0,
  onJumpToEmptyRequired,
  searchQuery,
  onSearchChange,
  extractionFilter,
  onExtractionFilterChange,
  extractedCount = 0,
  notExtractedCount = 0,
  viewFilter,
  onViewFilterChange,
  viewIndicatorStyle,
  viewTabRefs,
}) {
  return (
    <>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
            Extracted Data Fields
          </h3>
          <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-semibold tabular-nums bg-navy-50 text-navy-700 border border-navy-200 dark:bg-navy-400/10 dark:text-navy-300 dark:border-navy-400/30">
            {fieldCount}
          </span>
          {emptyRequiredCount > 0 && (
            <button
              onClick={onJumpToEmptyRequired}
              className="group/reqpill inline-flex items-center gap-1 pl-2 pr-2 h-6 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 bg-red-50 text-red-600 border border-red-200 dark:bg-red-400/10 dark:text-red-300 dark:border-red-400/30 hover:bg-red-100 hover:border-red-300 dark:hover:bg-red-400/20 hover:shadow-sm active:scale-95 transition-all duration-150"
              title="Click to jump to the next empty required field"
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              {emptyRequiredCount} Required Field
              {emptyRequiredCount !== 1 ? "s" : ""} Empty
              <ChevronRight className="h-3 w-3 shrink-0 transition-transform duration-150 group-hover/reqpill:translate-x-0.5" />
            </button>
          )}
        </div>
      </div>

      {/* Search + Filter + View toggle — wraps gracefully as the panel is resized */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="group/search relative flex-1 basis-[140px] max-w-[300px] min-w-[110px] h-8">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 group-focus-within/search:text-navy-500 dark:group-focus-within/search:text-navy-400 transition-colors" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search fields or sections..."
            className="w-full h-8 pl-8 pr-8 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-navy-400/40 focus:border-navy-400 dark:focus:border-navy-500 hover:border-gray-300 dark:hover:border-gray-500"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Clear field search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <select
          value={extractionFilter}
          onChange={(e) => onExtractionFilterChange(e.target.value)}
          className="shrink-0 h-8 text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm transition-colors hover:border-gray-300 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-navy-400/40 focus:border-navy-400"
        >
          <option value="all">Filter</option>
          <option value="extracted">Extracted ({extractedCount})</option>
          <option value="not_extracted">Not Extracted ({notExtractedCount})</option>
        </select>
        {/* Field type toggle — wraps to its own row when the panel is narrow, scrolls horizontally as a last resort */}
        <div className="w-full sm:w-auto order-last sm:order-none min-w-0 overflow-x-auto">
          <div className="relative inline-flex items-center h-8 rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-100/80 dark:bg-gray-800 shadow-sm">
            {viewIndicatorStyle && (
              <span
                className="absolute top-0.5 bottom-0.5 rounded-md bg-white dark:bg-gray-700 shadow-sm border border-gray-200 dark:border-gray-500 transition-[transform,width] duration-300 ease-out"
                style={{
                  transform: `translateX(${viewIndicatorStyle.left}px)`,
                  width: `${viewIndicatorStyle.width}px`,
                }}
              />
            )}
            {[
              { key: "all", label: "All Fields" },
              { key: "mandatory", label: "Mandatory" },
              { key: "custom", label: "Custom Fields" },
            ].map((tab) => (
              <button
                key={tab.key}
                ref={
                  viewTabRefs
                    ? (el) => {
                        viewTabRefs.current[tab.key] = el;
                      }
                    : undefined
                }
                onClick={() => onViewFilterChange(tab.key)}
                className={`relative z-10 px-3 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors duration-200 ${
                  viewFilter === tab.key
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
